import {fetchJSONMemoized} from '@gaia/utils/fetchJSON';
import fetchElevation from '@gaia/utils/gaia/fetchElevation';
import convertLngLat from '@gaia/utils/mapbox/convertLngLat';
import polyline from '@mapbox/polyline';
import turfDistance from '@turf/distance';
import turfLength from '@turf/length';
import memoize from 'lodash/memoize';

import getChunkedStraightLine from './getChunkedStraightLine';
import getStraightLine from './getStraightLine';

export interface FetchRoutedLineOptions {
    costing: Track['properties']['routing_mode'];
    fromLngLat: mapboxgl.LngLat;
    project: MapProject;
    toLngLat: mapboxgl.LngLat;
    omitElevation?: boolean;
}

interface Leg {
    shape: string;
    maneuvers: {
        begin_shape_index: number;
        end_shape_index: number;
        instruction: string;
        length: number;
        time: number;
        travel_mode: string;
        travel_type: string;
        type: number;
        verbal_pre_transition_instruction: string;
    }[];
    summary: {
        length: number;
        max_lat: number;
        max_lon: number;
        min_lat: number;
        min_lon: number;
        time: number;
    };
}

// Since the value returned by fetchRoutedLine is a promise, we are not able to access cached results
// synchronously. routedLineCache allows us to do so, thus preventing a switch from a routed line to
// a straight line and back to a routed line when a user sees a preview line and clicks
export const routedLineCache = new memoize.Cache();

async function conditionallyFetchElevation(
    geometry: GeoJSON.LineString,
    omitElevation: boolean
): Promise<GeoJSON.LineString> {
    if (omitElevation) return new Promise((resolve) => resolve(geometry));
    return fetchElevation(geometry);
}

async function fetchRoutedLine({
    fromLngLat,
    toLngLat,
    project,
    costing,
    omitElevation = false,
}: FetchRoutedLineOptions): Promise<GeoJSON.LineString> {
    if (costing === 'point-to-point' || !costing) {
        return await conditionallyFetchElevation(
            getChunkedStraightLine(fromLngLat, toLngLat),
            omitElevation
        );
    }

    const url = `https://routing.gaiagps.com/route?json=${encodeURIComponent(
        JSON.stringify({
            locations: [
                {lon: fromLngLat.lng, lat: fromLngLat.lat, type: 'break'},
                {lon: toLngLat.lng, lat: toLngLat.lat, type: 'break'},
            ],
            costing,
            costing_options: {pedestrian: {max_hiking_difficulty: 6}},
        })
    )}`;

    let response: any;
    try {
        response = await fetchJSONMemoized(url);
    } catch (e) {
        return await conditionallyFetchElevation(
            getStraightLine(fromLngLat, toLngLat),
            omitElevation
        );
    }

    if (response.status_code === 400) {
        return await conditionallyFetchElevation(
            getChunkedStraightLine(fromLngLat, toLngLat),
            omitElevation
        );
    }

    const leg: Leg = response.trip.legs[0];
    const geometry = polyline.toGeoJSON(leg.shape, 6);
    const {coordinates} = geometry;

    const straightLineLength = turfDistance(fromLngLat.toArray(), toLngLat.toArray(), {
        units: 'meters',
    });

    const routedLineLength = turfLength(
        {type: 'Feature', geometry, properties: {}},
        {
            units: 'meters',
        }
    );

    // Use a straight line if the routed length is 10x longer than the straight length
    if (routedLineLength > straightLineLength * 10) {
        return await conditionallyFetchElevation(
            getChunkedStraightLine(fromLngLat, toLngLat),
            omitElevation
        );
    }

    if (project) {
        const fromDist = project(convertLngLat(fromLngLat)).dist(
            project(convertLngLat(coordinates[0]))
        );

        const toDist = project(convertLngLat(toLngLat)).dist(
            project(convertLngLat(coordinates.at(-1)!))
        );

        const MAX_ENDPOINT_DEVIATION_PX = 20;
        if (fromDist >= MAX_ENDPOINT_DEVIATION_PX || toDist >= MAX_ENDPOINT_DEVIATION_PX) {
            return await conditionallyFetchElevation(
                getChunkedStraightLine(fromLngLat, toLngLat),
                omitElevation
            );
        }
    }

    const cacheKey = JSON.stringify([
        fromLngLat.toArray(),
        toLngLat.toArray(),
        costing,
        omitElevation,
    ]);

    routedLineCache.set(cacheKey, geometry);

    return await conditionallyFetchElevation(geometry, omitElevation);
}

export default memoize(
    fetchRoutedLine,
    ({fromLngLat, toLngLat, costing, omitElevation}: FetchRoutedLineOptions) => {
        return JSON.stringify([fromLngLat.toArray(), toLngLat.toArray(), costing, omitElevation]);
    }
);
